(() => {
  const STORAGE_KEY = 'geena-recently-viewed';
  const MAX_CARDS = 4;
  // Keep the current handle plus up to four prior handles, while rendering no more than four cards.
  const MAX_HISTORY = MAX_CARDS + 1;
  const HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]*$/i;

  function isValidHandle(value) {
    return typeof value === 'string' && HANDLE_PATTERN.test(value);
  }

  function validHandles(value) {
    if (!Array.isArray(value)) return [];
    return [...new Set(value.filter(isValidHandle))].slice(0, MAX_HISTORY);
  }

  function readHistory() {
    try {
      return { handles: validHandles(JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]')), available: true };
    } catch {
      return { handles: [], available: false };
    }
  }

  function writeHistory(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(validHandles(handles)));
      return true;
    } catch {
      return false;
    }
  }

  function remember(handle, handles) {
    writeHistory([handle, ...handles.filter((storedHandle) => storedHandle !== handle)].slice(0, MAX_HISTORY));
  }

  function productPath(rootUrl, handle) {
    try {
      const root = new URL(rootUrl || '/', window.location.origin);
      if (root.origin !== window.location.origin) return '';
      const basePath = root.pathname.endsWith('/') ? root.pathname : `${root.pathname}/`;
      return `${basePath}products/${encodeURIComponent(handle)}`;
    } catch {
      return '';
    }
  }

  function productJsonUrl(rootUrl, handle) {
    const path = productPath(rootUrl, handle);
    return path ? `${path}.js` : '';
  }

  function safeImageUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
    } catch {
      return '';
    }
  }

  function safeText(value, maxLength = 240) {
    return typeof value === 'string' ? value.replace(/[\u0000-\u001F\u007F]/g, '').trim().slice(0, maxLength) : '';
  }

  function formatPrice(cents, currency, locale) {
    const amount = Number(cents);
    if (!Number.isFinite(amount) || !/^[A-Z]{3}$/.test(currency || '')) return '';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount / 100);
    } catch {
      return '';
    }
  }

  async function fetchProduct(rootUrl, handle, signal) {
    const url = productJsonUrl(rootUrl, handle);
    if (!url) return null;

    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal,
      });
      if (!response.ok) return null;
      const product = await response.json();
      const title = safeText(product?.title);
      const image = safeImageUrl(product?.featured_image || product?.images?.[0]);
      const path = productPath(rootUrl, handle);
      if (!title || !image || !path || typeof product?.available !== 'boolean') return null;
      return { title, image, path, available: product.available, price: product.price };
    } catch {
      return null;
    }
  }

  function createCard(product, currency, locale) {
    const card = document.createElement('article');
    card.className = 'geena-recently-viewed__card';
    card.setAttribute('role', 'listitem');

    const imageLink = document.createElement('a');
    imageLink.className = 'geena-recently-viewed__image-link';
    imageLink.href = product.path;

    const image = document.createElement('img');
    image.className = 'geena-recently-viewed__image';
    image.src = product.image;
    image.alt = product.title;
    image.loading = 'lazy';
    image.decoding = 'async';
    imageLink.append(image);

    const title = document.createElement('h3');
    title.className = 'geena-recently-viewed__title';
    const titleLink = document.createElement('a');
    titleLink.href = product.path;
    titleLink.textContent = product.title;
    title.append(titleLink);

    card.append(imageLink, title);

    if (product.available === false) {
      const availability = document.createElement('p');
      availability.className = 'geena-recently-viewed__availability';
      availability.textContent = 'Availability to be confirmed';
      card.append(availability);
    } else {
      const priceText = formatPrice(product.price, currency, locale);
      if (priceText) {
        const price = document.createElement('p');
        price.className = 'geena-recently-viewed__price';
        price.textContent = priceText;
        card.append(price);
      }
    }

    return card;
  }

  class GeenaRecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.grid = this.querySelector('[data-recently-viewed-grid]');
      this.status = this.querySelector('[data-recently-viewed-status]');
      this.abortController = new AbortController();
      this.load();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.initialized = false;
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    async load() {
      const currentHandle = this.dataset.productHandle;
      if (!this.grid || !isValidHandle(currentHandle)) return;

      const history = readHistory();
      if (!history.available) return;

      const previousHandles = history.handles.filter((handle) => handle !== currentHandle).slice(0, MAX_CARDS);
      remember(currentHandle, history.handles);
      if (!previousHandles.length) return;

      const products = await Promise.all(
        previousHandles.map((handle) => fetchProduct(this.dataset.rootUrl, handle, this.abortController.signal))
      );
      if (!this.isConnected || this.abortController.signal.aborted) return;

      const cards = products.filter(Boolean).slice(0, MAX_CARDS).map((product) =>
        createCard(product, this.dataset.currency, document.documentElement.lang || undefined)
      );
      if (!cards.length) return;

      this.grid.replaceChildren(...cards);
      this.hidden = false;
      this.setStatus('Recently viewed products loaded.');
    }
  }

  if (!customElements.get('geena-recently-viewed')) {
    customElements.define('geena-recently-viewed', GeenaRecentlyViewed);
  }
})();
